'use client';
import { Button } from '@finn/design-system/atoms/button';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@finn/design-system/atoms/modal';
import { showToast } from '@finn/design-system/atoms/toast';
import { ToggleGroup } from '@finn/design-system/atoms/toggle';
import {
  AdditionalProtection,
  Deal,
  getAppliedAdditionalProtectionAddons,
  useInsuranceStore,
} from '@finn/platform-modules';
import { ModalKey } from '@finn/ua-modals';
import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { parseToHtml } from '@finn/ui-utils';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getAppliedInsuranceAddon,
  getInsuranceAddons,
} from '../helpers/addons';
import { AddonsResponse } from '../hooks/useGetAddons';
import { InsuranceToggleItem } from './InsuranceToggleItem';

export type InsuranceUpgradeModalData = {
  title: string;
  description: string;
  standardTitle: string;
  standardPrice: string;
  standardDescription: string;
  premiumTitle: string;
  premiumPrice: string;
  premiumDescription: string;
  footNote?: string;
  confirmCtaText: string;
  successToastTitle: string;
  successToastDescription: string;
  cancelCtaText: string;
};

type Props = {
  open: boolean;
  onClose(): void;
  dealId: number;
  deal?: Deal;
  addonsData: AddonsResponse;
  insuranceUpgradeContent: Record<string, any>;
  setReRenderKey?: Dispatch<SetStateAction<number>>;
  incrementRestoreCacheValue?: () => void;
  refreshOnSuccess?: boolean;
  mutateAddons?: any;
  configId?: number | string;
};

export const InsuranceUpgradeModal = ({
  open,
  onClose,
  dealId,
  deal,
  insuranceUpgradeContent,
  refreshOnSuccess,
  mutateAddons,
  addonsData,
  configId,
}: Props) => {
  const [insuranceOption, setInsuranceOption] = useState<string | undefined>(
    undefined
  );
  const [additionalProtections, setAdditionalProtections] = useState<string[]>(
    []
  );
  const updateInsuranceOption = useInsuranceStore(
    (state) => state.updateInsuranceOption
  );
  const updateAdditionalProtectionOptions = useInsuranceStore(
    (state) => state.updateAdditionalProtectionOptions
  );
  const modalData = insuranceUpgradeContent?.modal || {};
  const [submitting, setSubmitting] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const track = useTrackingStore((state) => state.track);

  const insuranceAddonsAll = getInsuranceAddons(addonsData);
  const appliedInsuranceAddon = getAppliedInsuranceAddon(addonsData);

  const currentVkDeductible = deal?.damage_deductible_vollkasko
    ? deal?.damage_deductible_vollkasko
    : 500000; // just a high number as default to include all when missing vollkasko amount

  // don't show downgrade options
  const insuranceAddons = (insuranceAddonsAll || []).filter(
    (addon) =>
      (addon?.pricing?.amount?.gross || 0) >=
        (appliedInsuranceAddon?.pricing?.amount?.gross || 0) &&
      (addon?.attributes?.coverage?.full || 0) <= currentVkDeductible
  );

  const currentlySelectedAddon =
    insuranceAddons.find((item) => item.addon_id === insuranceOption) ||
    appliedInsuranceAddon;

  const isZeroDeductible =
    currentlySelectedAddon?.attributes?.coverage?.full === 0;

  const appliedInsuranceAddonId = appliedInsuranceAddon?.addon_id;

  const appliedProtectionsIds = useMemo(
    () =>
      getAppliedAdditionalProtectionAddons(addonsData).map(
        (addon) => addon.addon_id
      ),
    [addonsData]
  );

  useEffect(() => {
    if (appliedInsuranceAddonId) {
      setInsuranceOption(appliedInsuranceAddonId);
    }
  }, [appliedInsuranceAddonId]);

  const onModalClose = useCallback(() => {
    onClose();
    mutateAddons?.();
    track(TrackingEventName.MODAL_CLOSED, { name: ModalKey.INSURANCE_UPGRADE });
    if (isSuccessful) {
      setIsSuccessful(false);
      if (refreshOnSuccess) {
        window.location.reload();
      }
    }
  }, [isSuccessful, onClose, track, refreshOnSuccess, mutateAddons]);

  const onConfirm = useCallback(async () => {
    setSubmitting(true);

    const addonsToAttach = additionalProtections.filter(
      (addonId) => !appliedProtectionsIds.includes(addonId)
    );
    const addonsToRemove = appliedProtectionsIds.filter(
      (addonId) => !additionalProtections.includes(addonId)
    );

    try {
      track(TrackingEventName.DEDUCTIBLE_OPTION_SUBMITTED, {
        additionalProps: { dealId, insuranceOption },
      });
      await Promise.all([
        updateInsuranceOption({
          dealId,
          addonId: insuranceOption,
        }),
        updateAdditionalProtectionOptions({
          dealId,
          addonsToAttach,
          addonsToRemove,
        }),
      ]);
      track(TrackingEventName.ADDON_ATTACHED, {
        name: insuranceOption,
        additionalProps: {
          addedProtections: addonsToAttach?.length
            ? addonsToAttach.join(', ')
            : '',
          removedProtections: addonsToRemove?.length
            ? addonsToAttach?.join(', ')
            : '',
        },
      });
      setIsSuccessful(true);
      track(TrackingEventName.DEDUCTIBLE_OPTION_SUCCESS, {
        additionalProps: { dealId, insuranceOption },
      });
    } catch (error) {
      track(TrackingEventName.DEDUCTIBLE_OPTION_ERROR, {
        additionalProps: { dealId, insuranceOption },
      });
      showToast({
        status: 'error',
        title: 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    additionalProtections,
    appliedProtectionsIds,
    track,
    dealId,
    insuranceOption,
    updateInsuranceOption,
    updateAdditionalProtectionOptions,
  ]);

  return (
    <Modal
      variant="small"
      open={open}
      onOpenChange={(isOpen) => {
        !isOpen && onModalClose();
      }}
    >
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {isSuccessful ? modalData?.successToastTitle : modalData?.title}
          </ModalTitle>
        </ModalHeader>
        <div className="flex flex-col gap-8">
          <div className="body-14-light">
            {parseToHtml(
              isSuccessful
                ? modalData?.successToastDescription
                : modalData?.description
            )}
          </div>
          {!isSuccessful && (
            <>
              <ToggleGroup
                value={insuranceOption}
                onValueChange={(val) => {
                  if (!val) return;
                  track(TrackingEventName.DEDUCTIBLE_OPTION_SELECTED, {
                    name: val,
                  });

                  setInsuranceOption(val);
                }}
                cols={1}
              >
                {insuranceAddons?.map((addon) => (
                  <InsuranceToggleItem
                    key={addon.addon_id}
                    addon={addon}
                    insuranceContent={insuranceUpgradeContent}
                  />
                ))}
              </ToggleGroup>
              <AdditionalProtection
                additionalProtections={additionalProtections}
                setAdditionalProtections={setAdditionalProtections}
                isZeroDeductible={isZeroDeductible}
                vehicleId={deal?.car?.external_product_id || configId}
                dealId={dealId}
              />
            </>
          )}
          {!isSuccessful && modalData?.footNote && (
            <div className="body-14-light">
              {parseToHtml(modalData?.footNote)}
            </div>
          )}
        </div>
        <ModalFooter>
          {isSuccessful ? (
            <div />
          ) : (
            <Button
              onClick={onConfirm}
              disabled={
                (insuranceOption === appliedInsuranceAddonId ||
                  !insuranceOption) &&
                ![...appliedProtectionsIds, ...additionalProtections].length
              }
              loading={submitting}
            >
              {modalData?.confirmCtaText}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
